import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignals, useSignalWatches, useUpdateStrategy, useBrokerStatus, useSetBrokerMode, useSetBrokerConfig } from '../services/api';
import { STRATEGY_LABELS } from '../constants/signalStrategies';
import { daysRunning, positionMark } from '../shared/strategyStats';
import { STOP_K_OPTIONS, ADVERSE_STOP_K, resolveStopK } from '../shared/bandSignals';
import { ownedByStrategy } from '../utils/strategyTabs';
import { useLogin } from '../contexts/LoginContext';

// Home-page widget for the user's STRATEGIES (issue #322): one card per
// strategy from GET /api/signals, each with its book-level record (compounded
// return at 1/cap of equity per entry, realized + including open marks, wins,
// average trade, running since) above that strategy's recent signals, open
// positions (entry, P&L, frozen target, bars vs time stop) and closed trades.
// One kind exists today — band reversion, whose universe is the user's
// hand-fitted watches added from a stock's Trading Range chart — and the model
// is the same for the list-driven kinds that follow: a strategy is universe ×
// entry rule × exit rule × allocation. Deliberately scoped to the USER'S OWN
// strategies — this is personal position state, not a generic signals feed.
// Inline styles per the project's Tailwind-reliability convention.

const fmtPrice = (v) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
// "2026-07-31" -> "Fri Jul 31". Pin to UTC: date-only strings parse as UTC
// midnight, which local formatting would shift back a day for US viewers.
const fmtDayLabel = (iso) => new Date(`${iso}T12:00:00Z`)
  .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
  .replace(',', ''); // "Mon, Aug 3" -> "Mon Aug 3"
const plColor = (v) => (v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#6b7280');
const fmtPl = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v}%`);
const EVENT_VERBS = { buy: 'Buy', sell: 'Sell', short: 'Short', cover: 'Cover' };
// An "entry" opens exposure (buy long / cover short); sell and short close or
// open the other way. Used for the row icon AND the collapsed-header pill, so
// keep the two readings from drifting apart.
const isEntryAction = (action) => action === 'buy' || action === 'cover';
// Green/red pill palette, shared by the action pills and the long/short side
// badges (inline per the project's Tailwind-reliability convention).
const POS_PILL = { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' };
const NEG_PILL = { background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };
const sidePill = (side) => (side === 'short' ? NEG_PILL : POS_PILL);
// Non-strategy exits get an amber badge — the position did NOT close on its
// own target or band, so the reason is worth surfacing:
//   stop loss     adverse move past entry ∓ the strategy's stop, default 2σ (#141, #417)
//   earnings      closed ahead of a confirmed report (issue #113)
//   time stop     mean strategy ran out its 2 × period clock
//   watch changed re-fitted or deleted watch, booked closed (issue #72)
// "stop loss" rather than "stop" so it cannot be misread as "time stop".
const EXIT_BADGES = {
  adverse: 'stop loss',
  earnings: 'earnings',
  time_stop: 'time stop',
  watch_changed: 'watch changed',
};
const MAX_EVENTS_SHOWN = 8;
const MAX_TRADES_SHOWN = 6;

const cardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '10px 12px',
  background: '#fff',
};

// Every ticker in the widget is a jump link into the stock table below: it
// expands that stock's row and scrolls to it (App.js handleFocusSymbol). Falls
// back to plain text when no handler is wired (mobile has no table).
const Ticker = ({ symbol, onSymbolClick, style }) => {
  if (!onSymbolClick) return <span style={style}>{symbol}</span>;
  return (
    <span
      role="button"
      tabIndex={0}
      title={`Show ${symbol} in the table`}
      onClick={(e) => { e.stopPropagation(); onSymbolClick(symbol); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onSymbolClick(symbol); }
      }}
      style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2, ...style }}
    >
      {symbol}
    </span>
  );
};

// Marks a card priced off an intraday quote rather than a settled close, so the
// two are never confused at a glance (issue #138).
const LIVE_PILL = {
  marginLeft: 5, fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 3,
  background: '#ecfdf5', color: '#047857', letterSpacing: '0.04em', verticalAlign: '1px',
};

// Price open exposure off the live quote whenever one is newer than the settled
// bar; the close still drives every scan/exit decision, so it stays visible in
// the tooltip. `positionMark` is the ONE definition (shared with fp_user's
// "incl. open" walk), so a card can never mix vintages — `live_price ??
// last_close` paired with `live_pl_pct ?? unrealized_pl_pct` would silently
// allow exactly that (issue #138).
// Marks a position the broker account actually holds (issue #348): blue for the paper account,
// green for live. Absent = the strategy holds it on paper only (predates the executor, or its
// entry was skipped).
const BROKER_PILL = {
  sandbox: { text: 'PAPER', background: '#eff6ff', color: '#1d4ed8' },
  live: { text: 'LIVE @ BROKER', background: '#f0fdf4', color: '#15803d' },
};
const PositionCard = ({ pos, held = null, brokerMode = 'off', manualExit = false, openedBeforeShortsOff = false, onSymbolClick }) => {
  const brokerPill = held && BROKER_PILL[brokerMode];
  const { isLive, price, pl } = positionMark(pos);
  // Broker side of the same position (issue #348): shares held, average fill (cost basis ÷ qty)
  // and the fill's slippage against the strategy's entry price — positive = paid more.
  const fill = held && held.quantity > 0 && held.cost_basis != null ? held.cost_basis / held.quantity : null;
  const slip = fill != null && pos.entry_price > 0 ? Math.round(((fill - pos.entry_price) / pos.entry_price) * 10000) / 100 : null;
  return (
  <div style={cardStyle}>
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
        <Ticker symbol={pos.ticker} onSymbolClick={onSymbolClick} />
        <span style={{
          marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
          background: sidePill(pos.side).background,
          color: sidePill(pos.side).color,
          textTransform: 'uppercase', letterSpacing: '0.03em',
        }}>
          {pos.side}
        </span>
        {brokerPill && (
          <span
            title={`Held at the broker: ${held.quantity} shares${fill != null ? ` at ${fmtPrice(fill)}` : ''}`}
            style={{ marginLeft: 4, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: brokerPill.background, color: brokerPill.color, letterSpacing: '0.03em' }}
          >
            {brokerPill.text}
          </span>
        )}
      </span>
      <span style={{ fontWeight: 700, fontSize: 14, color: plColor(pl) }}>
        {fmtPl(pl)}
      </span>
    </div>
    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
      {fmtPrice(pos.entry_price)} → {fmtPrice(price)}
      {isLive && (
        <span
          style={LIVE_PILL}
          title={`Intraday quote. Last close ${fmtPrice(pos.last_close)}${pos.last_close_date ? ` (${pos.last_close_date})` : ''} → ${fmtPl(pos.unrealized_pl_pct)}`}
        >
          LIVE
        </span>
      )}
      {pos.frozen_target != null && <> · target {fmtPrice(pos.frozen_target)}</>}
      {pos.adverse_stop_price != null && <> · stop {fmtPrice(pos.adverse_stop_price)}</>}
    </div>
    <div style={{ marginTop: 2, fontSize: 11, color: '#9ca3af' }}>
      {STRATEGY_LABELS[pos.strategy] || pos.strategy} · {pos.period}D
      {pos.bars_held != null && (
        <> · {pos.bars_held}{pos.time_stop_bars ? `/${pos.time_stop_bars}` : ''} bars</>
      )}
    </div>
    {/* Issue #397: with Shorts off the scan never enters a short, so an open one necessarily
        predates the switch — say so, or it reads as the setting having failed. */}
    {openedBeforeShortsOff && (
      <div style={{ marginTop: 3, fontSize: 11, color: '#92400e' }}>
        Opened before Shorts was turned off — exits on its own rule
      </div>
    )}
    {(held || manualExit) && (
      <div style={{ marginTop: 3, fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} title="At the broker">
        {held && <span>held {held.quantity} sh{fill != null ? ` · fill ${fmtPrice(fill)}` : ''}{slip != null ? <> · slip <span style={{ color: slip > 0 ? '#dc2626' : '#16a34a' }}>{slip >= 0 ? '+' : ''}{slip}%</span></> : ''}</span>}
        {manualExit && <span style={{ fontSize: 10, fontWeight: 600, padding: '0 6px', borderRadius: 999, border: '1px solid', ...NEG_PILL }}>MANUAL EXIT REQUIRED</span>}
      </div>
    )}
  </div>
  );
};

const EventRow = ({ ev, order = null, onSymbolClick, mobile = false }) => {
  const isEntry = isEntryAction(ev.action);
  const rowStyle = mobile
    ? { display: 'flex', flexDirection: 'column', gap: 2, padding: '5px 0', fontSize: 12.5, borderTop: '1px solid #f3f4f6', minWidth: 0, overflow: 'hidden' }
    : { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12.5, borderTop: '1px solid #f3f4f6', minWidth: 0, overflow: 'hidden' };
  const strategySpan = (
    <span style={{ color: '#9ca3af', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {STRATEGY_LABELS[ev.strategy] || ev.strategy} · {ev.period}D
    </span>
  );
  if (mobile) {
    return (
      <div style={rowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 16, textAlign: 'center' }}>{isEntry ? '🟢' : '🔴'}</span>
          <span style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
            {EVENT_VERBS[ev.action] || ev.action} <Ticker symbol={ev.ticker} onSymbolClick={onSymbolClick} />
          </span>
          <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>@ {fmtPrice(ev.price)}</span>
          {ev.target != null && <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>→ {fmtPrice(ev.target)}</span>}
          <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>{ev.date}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {strategySpan}
          <OrderPill order={order} />
        </div>
      </div>
    );
  }
  return (
    <div style={rowStyle}>
      <span style={{ width: 16, textAlign: 'center' }}>{isEntry ? '🟢' : '🔴'}</span>
      <span style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
        {EVENT_VERBS[ev.action] || ev.action} <Ticker symbol={ev.ticker} onSymbolClick={onSymbolClick} />
      </span>
      <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>@ {fmtPrice(ev.price)}</span>
      {ev.target != null && <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>→ {fmtPrice(ev.target)}</span>}
      {strategySpan}
      <OrderPill order={order} />
      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>{ev.date}</span>
    </div>
  );
};

// One closed round-trip (issue #49): entry → exit, realized P&L, exit reason.
const TradeRow = ({ t, onSymbolClick, mobile = false }) => {
  const exitBadge = EXIT_BADGES[t.exit_reason] && (
    <span style={{ fontSize: 10, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>
      {EXIT_BADGES[t.exit_reason]}
    </span>
  );
  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '5px 0', fontSize: 12.5, borderTop: '1px solid #f3f4f6', minWidth: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ticker symbol={t.ticker} onSymbolClick={onSymbolClick} style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }} />
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '0 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.03em',
            background: sidePill(t.side).background, color: sidePill(t.side).color,
          }}>
            {t.side}
          </span>
          <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>{fmtPrice(t.entry_price)} → {fmtPrice(t.exit_price)}</span>
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: plColor(t.pl_pct), whiteSpace: 'nowrap' }}>{fmtPl(t.pl_pct)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exitBadge}
          <span style={{ color: '#9ca3af', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
            {STRATEGY_LABELS[t.strategy] || t.strategy} · {t.period}D{t.bars_held != null ? ` · ${t.bars_held} bars` : ''}
          </span>
          <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>
            {t.exit_date}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12.5, borderTop: '1px solid #f3f4f6', minWidth: 0, overflow: 'hidden' }}>
      <Ticker symbol={t.ticker} onSymbolClick={onSymbolClick} style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }} />
      <span style={{
        fontSize: 10, fontWeight: 600, padding: '0 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.03em',
        background: sidePill(t.side).background, color: sidePill(t.side).color,
      }}>
        {t.side}
      </span>
      <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>{fmtPrice(t.entry_price)} → {fmtPrice(t.exit_price)}</span>
      <span style={{ fontWeight: 700, color: plColor(t.pl_pct), whiteSpace: 'nowrap' }}>{fmtPl(t.pl_pct)}</span>
      {exitBadge}
      <span style={{ color: '#9ca3af', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {STRATEGY_LABELS[t.strategy] || t.strategy} · {t.period}D{t.bars_held != null ? ` · ${t.bars_held} bars` : ''}
      </span>
      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>
        {t.entry_date ? `${t.entry_date} → ` : ''}{t.exit_date}
      </span>
    </div>
  );
};

const HintCard = ({ children }) => (
  <div style={{ ...cardStyle, background: '#f9fafb', color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '16px 12px' }}>
    {children}
  </div>
);

// One stat of a strategy's record: label over value, so a row of them reads as
// a strip rather than a run-on sentence. `sub` is a smaller line under the value.
const Stat = ({ label, value, color = '#111827', sub = null, title = null, children = null }) => (
  <div title={title || undefined} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <span style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{ fontSize: 14, fontWeight: 700, color, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {value}{children}
    </span>
    {sub && <span style={{ fontSize: 10.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>{sub}</span>}
  </div>
);

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
    {children}
  </div>
);

// ─── Broker connection (issue #347) ────────────────────────────────────────
// Rendered inside each strategy card, beside the cap it sizes from. One account
// serves every strategy today, so the block reads the same for each card; the
// slot figure is the only per-strategy number (equity / that card's cap).
// Renders nothing for anyone but the account owner (the worker 403s, the hook
// maps that to `eligible: false`).
const BROKER_MODE_LABELS = { off: 'Off', sandbox: 'Sandbox (paper)', live: 'Live' };
const fmtUsd = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtAt = (iso) => (iso ? new Date(iso.endsWith('Z') || iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// Order-outcome pill on a signal row (issue #348): what the executor did with that event.
const ORDER_PILL = {
  filled: { text: (o) => `filled ${o.exec_quantity ?? o.quantity} @ ${fmtPrice(o.avg_fill_price)}`, ...POS_PILL },
  partially_filled: { text: (o) => `partial ${o.exec_quantity ?? '?'}/${o.quantity}`, background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  submitted: { text: (o) => `order sent · ${o.quantity} ${o.order_type}${o.limit_price ? ` @ ${fmtPrice(o.limit_price)}` : ''}`, background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  open: { text: (o) => `order open · ${o.quantity} ${o.order_type}${o.limit_price ? ` @ ${fmtPrice(o.limit_price)}` : ''}`, background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  pending: { text: () => 'order pending', background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  claimed: { text: () => 'order claimed', background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  expired: { text: () => 'unfilled · expired', ...NEG_PILL },
  canceled: { text: () => 'canceled', ...NEG_PILL },
  rejected: { text: (o) => `rejected${o.error ? `: ${o.error}` : ''}`, ...NEG_PILL },
  error: { text: (o) => `error${o.error ? `: ${o.error}` : ''}`, ...NEG_PILL },
  manual_exit_required: { text: () => 'MANUAL EXIT REQUIRED', ...NEG_PILL },
  skipped: { text: (o) => SKIP_LABELS[o.skip_reason] || (o.skip_reason || 'skipped').replace(/^skipped_/, 'skipped: ').replace(/_/g, ' '), background: '#fffbeb', color: '#b45309', borderColor: '#fde68a' },
};
// Plain-English reasons for a signal the executor did not turn into an order (skip_reason values
// come from workers/broker-tradier/src/execution.js SKIP + the unreachable path in index.js).
const SKIP_LABELS = {
  skipped_not_held: 'no shares at broker · not sold',
  skipped_morning_entry: 'morning-scan entry · not bought',
  skipped_already_held: 'already held at broker',
  skipped_unsettled: 'settled cash short · not bought',
  skipped_qty_zero: 'slot below one share · not bought',
  skipped_cap: 'position cap full · not bought',
  skipped_short_strategy: 'short strategy · long-only account',
  skipped_no_sizing_basis: 'no balance data · not bought',
  broker_unreachable: 'broker unreachable',
};
const orderKey = (ticker, action, date) => `${ticker}|${action}|${date}`;
const OrderPill = ({ order }) => {
  if (!order) return null;
  const spec = ORDER_PILL[order.status] || ORDER_PILL.skipped;
  return (
    <span
      title={`${order.env === 'sandbox' ? 'Paper account' : 'Live account'} · ${order.status}${order.detail ? ` · ${order.detail}` : ''}${order.broker_order_id ? ` · order ${order.broker_order_id}` : ''}`}
      style={{ fontSize: 10, fontWeight: 600, padding: '0 6px', borderRadius: 999, whiteSpace: 'nowrap', border: '1px solid', background: spec.background, color: spec.color, borderColor: spec.borderColor, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {order.env === 'sandbox' ? 'paper · ' : ''}{spec.text(order)}
    </span>
  );
};

const ALLOCATION_STEPS = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100];

// One line per drift entry ("AAPL extra"), shared by the confirm dialog, the live preview and the
// reconcile summary below — the same `{ ticker, kind }` shape reconcile() returns in all three.
const formatDrift = (drift) => drift.map((d) => `${d.ticker} ${d.kind}`).join(', ');

// The one line the owner reads before switching live (issue #349): what the worker's read-only
// preview of the production account says right now. Rendered only while live is NOT the mode.
const liveConfirmText = (preview) => {
  const acct = preview?.account ? `Tradier account ${preview.account.tail} (${preview.account.type})` : 'the production Tradier account';
  const trip = preview?.would_trip?.length ? `\n\nWarning: reconcile would switch it straight back off — ${formatDrift(preview.would_trip)}.` : '';
  return `Switch to LIVE? From the next scan, real orders go to ${acct}: market buys at the close scan, market sells on every scan. Sandbox positions stay on paper.${trip}`;
};
// A Tradier token is per LOGIN, so it can see several accounts. Which one live trades is picked
// here, keyed by the tail (the only part of the number the browser ever holds) — shown in the
// pre-switch preview AND beside the connection while live, so the pick is never out of reach.
// Renders nothing when the token sees a single account.
const LiveAccountPicker = ({ current, accounts, setConfig }) => accounts?.length > 1 ? (
  <select
    aria-label="Live account"
    value={current}
    disabled={setConfig.isPending}
    onChange={(e) => setConfig.mutate({ live_account_tail: e.target.value.slice(-4) })}
    style={selectStyle}
    title="Which of this token's accounts live mode trades"
  >
    {accounts.map((a) => <option key={a.tail} value={a.tail} disabled={a.status !== 'active'}>{a.tail} · {a.type}{a.status !== 'active' ? ` · ${a.status}` : ''}</option>)}
  </select>
) : null;

const LivePreviewLine = ({ preview, setConfig }) => {
  if (preview.error) {
    return <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }} title="The production account, read but not traded. Live mode cannot be selected until it answers.">live account: {preview.error}</div>;
  }
  const { account, accounts, balances, positions, unmanaged, would_trip: wouldTrip } = preview;
  const basis = account.type === 'cash' ? `settled ${fmtUsd(balances?.cash_available)}` : `buying power ${fmtUsd(balances?.stock_buying_power)}`;
  const picker = <LiveAccountPicker current={account.tail} accounts={accounts} setConfig={setConfig} />;
  return (
    <div style={{ fontSize: 11, color: wouldTrip?.length ? '#dc2626' : '#15803d', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }} title="The production account, read but not traded. Unmanaged = held there in a ticker the strategy has no watch on; live mode never trades those.">
      <span>live account</span>{accounts?.length > 1 ? picker : <span>{account.tail}</span>}<span>· {account.type} · {account.status} · equity {fmtUsd(balances?.total_equity)} · {basis}
      {positions ? ` · ${positions} holding${positions === 1 ? '' : 's'}` : ' · no holdings'}
      {unmanaged?.length ? ` (${unmanaged.length} outside the strategy)` : ''}
      {wouldTrip?.length ? ` · would trip: ${formatDrift(wouldTrip)}` : ''}</span>
    </div>
  );
};

// A strategy's stop in entry-day σ (issue #417); 0 = off.
const stopLabel = (k) => (k ? `${k}σ` : 'off');
const selectStyle = { fontSize: 11, fontWeight: 400, padding: '1px 4px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', color: '#374151' };

const BrokerBlock = ({ data, setMode, setConfig, cap, mobile }) => {
  const { mode, account, accounts, balances, positions, error, reconcile, sandbox_ready: sandboxReady, live_blocked_reason: liveBlocked, live_preview: livePreview } = data;
  const allocation = data.allocation_pct ?? 100;
  const allocationOptions = ALLOCATION_STEPS.includes(allocation) ? ALLOCATION_STEPS : [...ALLOCATION_STEPS, allocation].sort((a, b) => a - b);
  const connected = mode !== 'off' && !error && account;
  const statusText = mode === 'off'
    ? 'Not connected'
    : error ? 'Broker unreachable' : `Tradier · ${mode}${account?.tail ? ` ${account.tail}` : ''}`;
  const statusColor = mode === 'off' ? '#6b7280' : error ? '#dc2626' : mode === 'live' ? '#15803d' : '#2563eb';
  // Same arithmetic as the worker's planner: equity × allocation ÷ cap.
  const slot = balances?.total_equity != null && cap ? (balances.total_equity * allocation) / 100 / cap : null;
  const held = positions?.length || 0;
  return (
    <div style={{ marginTop: 10 }}>
      <SectionLabel>Broker</SectionLabel>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: mobile ? 14 : 22, flexWrap: 'wrap', rowGap: 8 }}>
        <Stat label="Connection" value={statusText} color={statusColor} sub={error ? null : (account?.type ? `${account.type} account` : null)}>
          <select
            aria-label="Broker mode"
            value={mode}
            disabled={setMode.isPending}
            onChange={(e) => {
              // Live places real orders in the real account from the next scan: one explicit
              // confirmation, naming the account, before the worker is asked (issue #349).
              if (e.target.value === 'live' && !window.confirm(liveConfirmText(livePreview))) return;
              setMode.mutate(e.target.value);
            }}
            style={selectStyle}
          >
            <option value="off">{BROKER_MODE_LABELS.off}</option>
            <option value="sandbox" disabled={!sandboxReady} title={sandboxReady ? undefined : 'TRADIER_SANDBOX_TOKEN is not set on the worker'}>{BROKER_MODE_LABELS.sandbox}</option>
            <option value="live" disabled={Boolean(liveBlocked)} title={liveBlocked || undefined}>{BROKER_MODE_LABELS.live}</option>
          </select>
          {mode === 'live' && <LiveAccountPicker current={account?.tail} accounts={accounts} setConfig={setConfig} />}
        </Stat>
        {connected && (
          <>
            <Stat label="Equity" value={fmtUsd(balances.total_equity)} sub={held ? `${held} position${held === 1 ? '' : 's'} held` : 'no positions held'} title="Total account equity at the broker (live read)" />
            <Stat
              label={`Slot · 1/${cap}`}
              value={fmtUsd(slot)}
              sub={`${allocation}% of equity ÷ ${cap}`}
              title={`Size of one entry: equity × allocation share ÷ position cap. The allocation share is how much of the account the strategy may deploy across all ${cap} slots.`}
            >
              <select
                aria-label="Share of account equity the strategy may deploy"
                value={allocation}
                disabled={setConfig.isPending}
                onChange={(e) => setConfig.mutate({ allocation_pct: Number(e.target.value) })}
                style={selectStyle}
              >
                {allocationOptions.map((n) => <option key={n} value={n}>{n}%</option>)}
              </select>
            </Stat>
            {account.type === 'margin' ? (
              <Stat
                label="Buying power"
                value={fmtUsd(balances.stock_buying_power)}
                sub="margin account · no settlement cap"
                title="A margin account has no settled-cash constraint; entries are capped by stock buying power instead. The real IRA is a cash account and will show settled cash here."
              />
            ) : (
              <Stat
                label="Settled cash"
                value={fmtUsd(balances.cash_available)}
                sub={balances.unsettled_funds != null ? `unsettled ${fmtUsd(balances.unsettled_funds)}` : null}
                title="Cash available to trade without a good-faith violation. Entries are capped by this, never by total equity."
              />
            )}
          </>
        )}
      </div>
      {error && (
        <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>
          {error.message}{error.at ? ` · ${fmtAt(error.at)}` : ''}{data.failure_count > 1 ? ` · ${data.failure_count} consecutive failures` : ''}
        </div>
      )}
      {setMode.isError && <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>{setMode.error.message}</div>}
      {livePreview && <LivePreviewLine preview={livePreview} setConfig={setConfig} />}
      {setConfig.isError && <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>{setConfig.error.message}</div>}
      {reconcile && (() => {
        // `missing` / `extra` are actionable (the worker already switched itself off); `untracked`
        // (a position the strategy opened before the executor was on) and `unmanaged` (a broker
        // holding the strategy has no watch on — the executor can't touch it) are informational.
        // Mirrors `isActionable` in workers/broker-tradier/src/execution.js — the panel can't
        // import worker code (CRA can't import outside src/), so this predicate is kept in sync
        // by hand; re-check it here if that one changes.
        const actionable = reconcile.drift.filter((d) => d.kind === 'missing' || d.kind === 'extra');
        const untracked = reconcile.drift.filter((d) => d.kind === 'untracked').length;
        const unmanaged = reconcile.drift.filter((d) => d.kind === 'unmanaged').length;
        return (
          <div style={{ fontSize: 11, color: actionable.length ? '#dc2626' : '#9ca3af', marginTop: 4 }} title="Strategy book vs. broker positions, checked after every scan's fill poll. Untracked = opened before the executor was switched on; the executor will not exit those. Unmanaged = held at the broker in a ticker the strategy has no watch on; the executor never trades those.">
            {actionable.length
              ? `drift: ${formatDrift(actionable)}`
              : 'book in sync with broker'}
            {untracked ? ` · ${untracked} position${untracked === 1 ? '' : 's'} predate${untracked === 1 ? 's' : ''} the executor (not tracked)` : ''}
            {unmanaged ? ` · ${unmanaged} holding${unmanaged === 1 ? '' : 's'} outside the strategy (not managed)` : ''} · {fmtAt(reconcile.at)}
          </div>
        );
      })()}
    </div>
  );
};

// Everything a strategy owns, already filtered to it by the parent.
const StrategyCard = ({ strategy, watches, events, positions, trades, asOf, watchMax, mobile, onSymbolClick, email }) => {
  const updateStrategy = useUpdateStrategy();
  const { data: broker } = useBrokerStatus(true, email);
  const setBrokerMode = useSetBrokerMode(email);
  const setBrokerConfig = useSetBrokerConfig(email);
  const brokerOn = broker && broker.eligible !== false;
  // Executor ledger keyed the way the signals payload identifies an event (ticker+action+date —
  // the payload carries no id), and the account's holdings by symbol for the position cards.
  const ordersByEvent = new Map((brokerOn ? broker.orders : []).map((o) => [orderKey(o.ticker, o.action, o.event_date), o]));
  const heldBySymbol = new Map((brokerOn ? broker.positions : []).map((p) => [p.symbol, p]));
  const manualExit = new Set((brokerOn ? broker.orders : []).filter((o) => o.status === 'manual_exit_required').map((o) => o.ticker));
  const st = strategy.stats || {};
  const cap = strategy.position_cap;
  const allowShort = strategy.allow_short !== false;
  // 0 = off. Already resolved server-side; resolving again is idempotent and covers
  // a payload from an fp_user that predates the field — same call as the chart's.
  const stopK = resolveStopK(strategy.stop_k);
  const openShorts = positions.filter((p) => p.side === 'short').length;
  const started = strategy.started_at ? strategy.started_at.slice(0, 10) : null;
  // "Running" is measured to the last processed bar, the same clock the record
  // is kept in — not to the wall clock, which would tick over a weekend with
  // no session behind it.
  const running = started ? daysRunning(started, asOf || started) : null;
  const slotsNote = `Each entry is sized at 1/${cap} of equity (the position cap in force now — its history is not kept, so changing the cap changes this figure). Idle slots earn nothing; closed trades compound in exit order.`;
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: mobile ? '10px 12px' : '10px 14px', background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: mobile ? 14 : 22, flexWrap: 'wrap', rowGap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', whiteSpace: 'nowrap' }}>{strategy.name}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {watches.length} stock{watches.length === 1 ? '' : 's'} watched
          </div>
        </div>
        <Stat
          label={`Realized · at ${cap} slots`}
          value={fmtPl(st.realized_return_pct)}
          color={plColor(st.realized_return_pct)}
          sub={st.incl_open_return_pct != null ? <>incl. open <span style={{ fontWeight: 600, color: plColor(st.incl_open_return_pct) }}>{fmtPl(st.incl_open_return_pct)}</span></> : null}
          title={`Compounded return of the whole book over ${st.trades || 0} closed trade${st.trades === 1 ? '' : 's'}. ${slotsNote}`}
        />
        <Stat
          label="Wins"
          value={st.trades ? `${st.wins}/${st.trades}` : '—'}
          sub={st.avg_pl_pct != null ? <>avg trade <span style={{ fontWeight: 600, color: plColor(st.avg_pl_pct) }}>{fmtPl(st.avg_pl_pct)}</span></> : null}
          title="Closed trades that ended positive, and the average per-trade P&L"
        />
        <Stat
          label="Running"
          value={running != null ? `${running} day${running === 1 ? '' : 's'}` : '—'}
          sub={started ? `since ${fmtDayLabel(started)}` : null}
          title={asOf ? `From the strategy's first watch to the last processed session (${asOf})` : undefined}
        />
        {/* Portfolio cap (issue #154): how many watches may hold a position AT
            ONCE — distinct from the watch cap, which limits how many tickers can
            be watched at all. Sits on the count it governs so the remaining
            headroom is obvious; per strategy since #322. */}
        <Stat label="Open" value={`${positions.length} of ${cap}`} title="Open positions against this strategy's cap">
          <select
            aria-label={`Maximum open positions for ${strategy.name}`}
            value={cap}
            disabled={updateStrategy.isPending}
            onChange={(e) => updateStrategy.mutate({ strategyId: strategy.id, positionCap: Number(e.target.value) })}
            style={selectStyle}
          >
            {Array.from({ length: watchMax || 20 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>max {n}</option>
            ))}
          </select>
          {positions.length >= cap && (
            <span
              style={{ fontSize: 10, fontWeight: 600, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 5px' }}
              title="New signals cannot open until a position closes. When more fire than there are free slots, the most volatile candidate wins."
            >
              full
            </span>
          )}
          {updateStrategy.isError && <span style={{ fontSize: 10.5, fontWeight: 400, color: '#dc2626' }}>save failed</span>}
        </Stat>
        {/* Shorts (issue #352): whether short-shaped watches (short → mean, short → band) may
            ENTER. The connected account is an IRA and cannot short, so a short entry would be a
            paper-only position burning a slot the broker can never mirror. Exits are unaffected. */}
        <Stat
          label="Shorts"
          value={allowShort ? 'allowed' : 'off'}
          color={allowShort ? '#111827' : '#6b7280'}
          sub={allowShort ? null : openShorts ? `no new shorts · ${openShorts} still open` : 'short watches never enter'}
          title="Whether short-shaped watches may open positions. Turn off when the connected broker account cannot short (an IRA); open shorts still exit on their own rule."
        >
          <select
            aria-label={`Allow short entries for ${strategy.name}`}
            value={allowShort ? 'on' : 'off'}
            disabled={updateStrategy.isPending}
            onChange={(e) => updateStrategy.mutate({ strategyId: strategy.id, allowShort: e.target.value === 'on' })}
            style={selectStyle}
          >
            <option value="on">allowed</option>
            <option value="off">off</option>
          </select>
        </Stat>
        {/* Stop loss (issue #417): how far a position may move against its entry, in
            multiples of the entry day's volatility, before the scan closes it. Applies
            to open positions too, from the next scan — each position card shows its level. */}
        <Stat
          label="Stop loss"
          value={stopLabel(stopK)}
          color={stopK ? '#111827' : '#6b7280'}
          sub={stopK ? 'vs entry-day volatility' : 'own exit rules only'}
          title={`Closes a position once its close moves this many standard deviations of the entry day's band against the entry price, so the distance scales with each stock's volatility. ${ADVERSE_STOP_K}σ is the fitted default; tighter stops cut losers sooner but turn more would-be winners into small losses. Off = positions exit only on their band, target, time or earnings rule. Applies to open positions from the next scan.`}
        >
          <select
            aria-label={`Stop loss for ${strategy.name}`}
            value={stopK}
            disabled={updateStrategy.isPending}
            onChange={(e) => updateStrategy.mutate({ strategyId: strategy.id, stopK: Number(e.target.value) })}
            style={selectStyle}
          >
            {STOP_K_OPTIONS.map((k) => (
              <option key={k} value={k}>{stopLabel(k)}{k === ADVERSE_STOP_K ? ' (default)' : ''}</option>
            ))}
          </select>
        </Stat>
      </div>

      {brokerOn && <BrokerBlock data={broker} setMode={setBrokerMode} setConfig={setBrokerConfig} cap={cap} mobile={mobile} />}

      <div style={{ marginTop: 10 }}>
        <SectionLabel>Recent signals</SectionLabel>
        {events.length
          ? events.map((ev, i) => <EventRow key={`${ev.ticker}-${ev.date}-${ev.action}-${i}`} ev={ev} order={ordersByEvent.get(orderKey(ev.ticker, ev.action, ev.date))} onSymbolClick={onSymbolClick} mobile={mobile} />)
          : (
            <div style={{ fontSize: 12.5, color: '#9ca3af', paddingTop: 4 }}>
              No signals{asOf ? ` off the ${fmtDayLabel(asOf)} close` : ' today'}.
            </div>
          )}
      </div>

      <div style={{ marginTop: 10 }}>
        <SectionLabel>Open positions{positions.length ? ` (${positions.length})` : ''}</SectionLabel>
        {positions.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 160 : 220}px, 1fr))`, gap: 8 }}>
            {positions.map((pos) => <PositionCard key={pos.ticker} pos={pos} held={heldBySymbol.get(pos.ticker)} brokerMode={brokerOn ? broker.mode : 'off'} manualExit={manualExit.has(pos.ticker)} openedBeforeShortsOff={!allowShort && pos.side === 'short'} onSymbolClick={onSymbolClick} />)}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: '#6b7280' }}>None — every watched stock is flat.</div>
        )}
      </div>

      {trades.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <SectionLabel>Closed trades</SectionLabel>
          {trades.slice(0, MAX_TRADES_SHOWN).map((t, i) => (
            <TradeRow key={`${t.ticker}-${t.exit_date}-${i}`} t={t} onSymbolClick={onSymbolClick} mobile={mobile} />
          ))}
          {trades.length > MAX_TRADES_SHOWN && (
            <div style={{ fontSize: 11, color: '#9ca3af', paddingTop: 4 }}>
              +{trades.length - MAX_TRADES_SHOWN} more in the signals feed
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// mobile: full-width tab content with login CTA + empty-state hints.
// Desktop renders nothing until there is something to show (space is scarce).
// The storage key predates the strategies framing and is deliberately NOT
// renamed — it is an id, not a label, and renaming it would reset every
// user's collapse choice.
const EXPANDED_KEY = 'signalWatchesWidgetExpanded';

const StrategiesWidget = ({ loggedInUser, mobile = false, onSymbolClick = null }) => {
  const { triggerLogin } = useLogin();
  const { data, isLoading, dataUpdatedAt } = useSignals(!!loggedInUser, loggedInUser?.email);
  // Issue #396: the broker block re-reads whenever the signals land, so the panel never pairs
  // a current broker account with an old strategy book (seen: the broker holding DOCN beside a
  // positions list two days old). The first load is skipped — the broker block fetches on mount.
  const queryClient = useQueryClient();
  const signalsSeenAt = useRef(0);
  useEffect(() => {
    if (signalsSeenAt.current && dataUpdatedAt !== signalsSeenAt.current) {
      queryClient.invalidateQueries({ queryKey: ['broker', 'status'] });
    }
    signalsSeenAt.current = dataUpdatedAt;
  }, [dataUpdatedAt, queryClient]);
  // The watch cap (`max`, how many tickers may be watched at all) lives on the
  // watches endpoint — it is configuration, not signal state. React Query
  // shares the cache key with the notifications modal, so mounting both costs
  // one request.
  const { data: watchData } = useSignalWatches(!!loggedInUser, loggedInUser?.email);
  // Desktop defaults to COLLAPSED (vertical space above the stock table is
  // scarce); the choice persists. Mobile has a dedicated tab, always expanded.
  const [expanded, setExpanded] = useState(() => localStorage.getItem(EXPANDED_KEY) === '1');
  const toggleExpanded = () => setExpanded((v) => {
    localStorage.setItem(EXPANDED_KEY, v ? '0' : '1');
    return !v;
  });

  if (!loggedInUser) {
    if (!mobile) return null;
    return (
      <HintCard>
        Sign in to run a band-reversion strategy on your watched stocks.
        <div style={{ marginTop: 10 }}>
          <button
            onClick={() => triggerLogin()}
            style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Sign in
          </button>
        </div>
      </HintCard>
    );
  }

  if (isLoading || !data) {
    return mobile ? <HintCard>Loading your strategies…</HintCard> : null;
  }

  const strategies = data.strategies || [];
  const watches = data.watches || [];
  const positions = data.positions || [];
  const trades = data.closed_trades || [];
  // Recent signals = the latest processed session and anything newer (issue
  // #71); older events (incl. backfill replays) live in the signals feed, not
  // here. `>=` rather than `===` because an intraday exit scan records today's
  // event WITHOUT advancing the watermark — as_of only moves to today at the
  // close scan, and a mid-session close must stay visible until then.
  // No provisional/confirmed de-duplication any more (issue #116): every event
  // the API returns was durably recorded exactly once.
  const events = (data.events || [])
    .filter((e) => !data.as_of || e.date >= data.as_of)
    .slice(0, MAX_EVENTS_SHOWN);

  if (!strategies.length) {
    if (!mobile) return null;
    return (
      <HintCard>
        No strategies yet — open any stock&apos;s Trading Range chart and click 🔔 Watch to start a band-reversion strategy on it.
      </HintCard>
    );
  }

  // "Which rows belong to strategy S" is ONE shared definition (strategyTabs.js)
  // with the My Lists strategy tab, so this card's count and that list's rows agree.
  const ownedBy = (s) => ownedByStrategy(s, strategies);
  const showBody = mobile || expanded;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: showBody ? '12px 14px' : '8px 14px', marginBottom: mobile ? 16 : 12 }}>
      <div
        onClick={mobile ? undefined : toggleExpanded}
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', cursor: mobile ? 'default' : 'pointer', marginBottom: showBody ? 8 : 0 }}
      >
        {!mobile && <span style={{ fontSize: 10, color: '#9ca3af' }}>{expanded ? '▼' : '▶'}</span>}
        <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>🎯 My Strategies</span>
        {!showBody && (
          <>
            {strategies.map((s) => (
              <span key={`s-${s.id}`} style={{ fontSize: 11.5, color: '#374151', whiteSpace: 'nowrap' }} title={`${s.name}: realized return at ${s.position_cap} slots`}>
                {s.name}{' '}
                <span style={{ fontWeight: 700, color: plColor(s.stats?.realized_return_pct) }}>{fmtPl(s.stats?.realized_return_pct)}</span>
              </span>
            ))}
            {events.map((ev, i) => {
              const pill = isEntryAction(ev.action) ? POS_PILL : NEG_PILL;
              return (
                <span
                  key={`t-${ev.ticker}-${ev.action}-${i}`}
                  style={{
                    fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                    background: pill.background, color: pill.color, border: `1px solid ${pill.borderColor}`,
                  }}
                >
                  {EVENT_VERBS[ev.action] || ev.action}{' '}
                  <Ticker symbol={ev.ticker} onSymbolClick={onSymbolClick} /> @ {fmtPrice(ev.price)}
                </span>
              );
            })}
            {positions.map((pos) => {
              // Same selection as the expanded card: toggling the disclosure
              // must never change the number.
              const { pl } = positionMark(pos);
              return (
                <span key={`p-${pos.ticker}`} style={{ fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  <Ticker symbol={pos.ticker} onSymbolClick={onSymbolClick} /> <span style={{ fontWeight: 600, color: plColor(pl) }}>{fmtPl(pl)}</span>
                </span>
              );
            })}
          </>
        )}
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {strategies.length} strateg{strategies.length === 1 ? 'y' : 'ies'}{data.as_of ? ` · through ${fmtDayLabel(data.as_of)} close` : ''}
        </span>
      </div>

      {showBody && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {strategies.map((s) => (
            <StrategyCard
              email={loggedInUser?.email}
              key={s.id}
              strategy={s}
              watches={watches.filter(ownedBy(s))}
              events={events.filter(ownedBy(s))}
              positions={positions.filter(ownedBy(s))}
              trades={trades.filter(ownedBy(s))}
              asOf={data.as_of}
              watchMax={watchData?.max}
              mobile={mobile}
              onSymbolClick={onSymbolClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(StrategiesWidget);
